import React, { useState, useEffect } from 'react';
import {
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Box,
} from '@mui/material';
import { FieldMapping } from '../../services/fieldMapping.service';
import { isFieldHidden, isFieldRequired } from '../../utils/formUtils';

interface JobSearchInformationProps {
  data?: any;
  onChange?: (data: any) => void;
  formData?: any;
  onFormDataChange?: (data: any) => void;
  fieldMappings?: FieldMapping[];
  isRequired?: boolean;
  readOnly?: boolean;
}

interface FormData {
  looked_for_work: string;
  employer1_name: string;
  employer1_job_title: string;
  employer1_date: string;
  employer2_name: string;
  employer2_job_title: string;
  employer2_date: string;
  employer3_name: string;
  employer3_job_title: string;
  employer3_date: string;
}

const JobSearchInformation: React.FC<JobSearchInformationProps> = ({
  data,
  onChange,
  formData: initialFormData,
  onFormDataChange,
  fieldMappings = [],
  readOnly = false,
}) => {
  const actualFormData = initialFormData || data || {};
  const actualOnChange =
    onFormDataChange ||
    onChange ||
    ((_data: any) => { /* noop */ });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isFieldHiddenLocal  = (f: string) => isFieldHidden(f, 'job_search_information', fieldMappings);
  const isFieldRequiredLocal = (f: string) => isFieldRequired(f, 'job_search_information', fieldMappings);
  const [formData, setFormData] = useState<FormData>({
    looked_for_work: '',
    employer1_name: '',
    employer1_job_title: '',
    employer1_date: '',
    employer2_name: '',
    employer2_job_title: '',
    employer2_date: '',
    employer3_name: '',
    employer3_job_title: '',
    employer3_date: '',
    ...actualFormData,
  });

  useEffect(() => {
    setFormData((prev: FormData) => ({ ...prev, ...actualFormData }));
  }, [actualFormData]);

  const handleChange = (field: string, value: any) => {
    if (readOnly) return; // Prevent changes in read-only mode
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    actualOnChange(updatedData);
  };

  const renderEmployerEntry = (employerNumber: number) => {
    const nameField = `employer${employerNumber}_name`;
    const titleField = `employer${employerNumber}_job_title`;
    const dateField = `employer${employerNumber}_date`;

    return (
      <Box
        key={employerNumber}
        sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}
      >
        <Typography variant='h6' gutterBottom>
          Employer #{employerNumber}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label='Name of employer'
            value={formData[nameField as keyof FormData] as string}
            onChange={e => handleChange(nameField, e.target.value)}
            required={isFieldRequiredLocal(nameField)}
          />
          <TextField
            fullWidth
            label='Job Title'
            value={formData[titleField as keyof FormData] as string}
            onChange={e => handleChange(titleField, e.target.value)}
          />
          <TextField
            fullWidth
            label='Date'
            type='date'
            value={formData[dateField as keyof FormData] as string}
            onChange={e => handleChange(dateField, e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant='h5' gutterBottom>
        Job Search Information
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Job Search Status */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Job Search Status
          </Typography>
          <FormControl component='fieldset' sx={{ mb: 2 }}>
            <Typography variant='subtitle1' gutterBottom>
              Have you looked for work?
            </Typography>
            <RadioGroup
              value={formData.looked_for_work}
              onChange={e => handleChange('looked_for_work', e.target.value)}
            >
              <FormControlLabel value='yes' control={<Radio />} label='Yes' />
              <FormControlLabel value='no' control={<Radio />} label='No' />
            </RadioGroup>
          </FormControl>
        </Box>

        {formData.looked_for_work === 'yes' && (
          <Box>
            <Typography variant='h6' gutterBottom>
              Job Search Details
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              Please provide information about employers you have contacted for
              work:
            </Typography>

            {renderEmployerEntry(1)}
            {renderEmployerEntry(2)}
            {renderEmployerEntry(3)}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default JobSearchInformation;
